import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';

const IntruderOptions = ({ grepMatch, onGrepMatchChange, grepExtract, onGrepExtractChange, throttle, onThrottleChange, isDarkMode }) => {
  const [newMatchRule, setNewMatchRule] = useState({ name: '', pattern: '', location: 'response_body', negative: false });
  const [newExtractRule, setNewExtractRule] = useState({ name: '', pattern: '', location: 'response_body', group: 0 });

  const addMatchRule = () => {
    if (!newMatchRule.pattern) return;
    onGrepMatchChange([...grepMatch, { ...newMatchRule }]);
    setNewMatchRule({ name: '', pattern: '', location: 'response_body', negative: false });
  };

  const removeMatchRule = (index) => {
    onGrepMatchChange(grepMatch.filter((_, i) => i !== index));
  };

  const addExtractRule = () => {
    if (!newExtractRule.pattern) return;
    onGrepExtractChange([...grepExtract, { ...newExtractRule }]);
    setNewExtractRule({ name: '', pattern: '', location: 'response_body', group: 0 });
  };

  const removeExtractRule = (index) => {
    onGrepExtractChange(grepExtract.filter((_, i) => i !== index));
  };

  const inputClass = `text-xs rounded border px-2 py-1.5 ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`;
  const labelClass = `text-[10px] font-semibold block mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`;
  const sectionClass = `p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`;

  return (
    <div className="space-y-4">
      {/* Throttle Settings */}
      <div className={sectionClass}>
        <h4 className={`text-xs font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Request Throttle
        </h4>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className={labelClass}>Requests/sec</label>
            <input
              type="number"
              value={throttle.requestsPerSecond ?? 10}
              onChange={e => onThrottleChange({ ...throttle, requestsPerSecond: Number(e.target.value) })}
              min={1}
              max={1000}
              className={`w-full ${inputClass}`}
            />
          </div>
          <div>
            <label className={labelClass}>Max concurrent</label>
            <input
              type="number"
              value={throttle.maxConcurrent ?? 1}
              onChange={e => onThrottleChange({ ...throttle, maxConcurrent: Number(e.target.value) })}
              min={1}
              max={50}
              className={`w-full ${inputClass}`}
            />
          </div>
          <div>
            <label className={labelClass}>Delay between (ms)</label>
            <input
              type="number"
              value={throttle.delayBetweenRequests ?? 100}
              onChange={e => onThrottleChange({ ...throttle, delayBetweenRequests: Number(e.target.value) })}
              min={0}
              max={60000}
              className={`w-full ${inputClass}`}
            />
          </div>
        </div>
      </div>

      {/* Grep Match */}
      <div className={sectionClass}>
        <h4 className={`text-xs font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Grep - Match
          <span className={`font-normal ml-2 text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Flag results matching these patterns
          </span>
        </h4>

        {/* Existing rules */}
        {grepMatch.length > 0 && (
          <table className="w-full text-xs mb-3">
            <thead>
              <tr className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>
                <th className="text-left px-2 py-1 text-[10px]">Name</th>
                <th className="text-left px-2 py-1 text-[10px]">Pattern</th>
                <th className="text-left px-2 py-1 text-[10px]">Location</th>
                <th className="text-left px-2 py-1 text-[10px]">Negative</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {grepMatch.map((rule, i) => (
                <tr key={i} className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <td className={`px-2 py-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{rule.name || '-'}</td>
                  <td className={`px-2 py-1 font-mono ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{rule.pattern}</td>
                  <td className={`px-2 py-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{rule.location}</td>
                  <td className={`px-2 py-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{rule.negative ? 'Yes' : 'No'}</td>
                  <td>
                    <button onClick={() => removeMatchRule(i)} className="text-red-400 hover:text-red-300 p-1">
                      <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Add new match rule */}
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className={labelClass}>Name</label>
            <input
              value={newMatchRule.name}
              onChange={e => setNewMatchRule(p => ({ ...p, name: e.target.value }))}
              className={`w-full ${inputClass}`}
              placeholder="Rule name"
            />
          </div>
          <div className="flex-1">
            <label className={labelClass}>Pattern (regex)</label>
            <input
              value={newMatchRule.pattern}
              onChange={e => setNewMatchRule(p => ({ ...p, pattern: e.target.value }))}
              className={`w-full ${inputClass} font-mono`}
              placeholder="error|fail|invalid"
            />
          </div>
          <div>
            <label className={labelClass}>Location</label>
            <select
              value={newMatchRule.location}
              onChange={e => setNewMatchRule(p => ({ ...p, location: e.target.value }))}
              className={inputClass}
            >
              <option value="response_body">Response body</option>
              <option value="response_headers">Response headers</option>
              <option value="status_code">Status code</option>
            </select>
          </div>
          <div>
            <label className={`${labelClass} flex items-center gap-1`}>
              <input
                type="checkbox"
                checked={newMatchRule.negative}
                onChange={e => setNewMatchRule(p => ({ ...p, negative: e.target.checked }))}
                className={`rounded ${isDarkMode ? 'accent-orange-500' : ''}`}
              />
              Negative
            </label>
          </div>
          <button
            onClick={addMatchRule}
            disabled={!newMatchRule.pattern}
            className="px-3 py-1.5 rounded text-xs bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>
      </div>

      {/* Grep Extract */}
      <div className={sectionClass}>
        <h4 className={`text-xs font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Grep - Extract
          <span className={`font-normal ml-2 text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Extract data from responses using regex capture groups
          </span>
        </h4>

        {/* Existing rules */}
        {grepExtract.length > 0 && (
          <table className="w-full text-xs mb-3">
            <thead>
              <tr className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>
                <th className="text-left px-2 py-1 text-[10px]">Name</th>
                <th className="text-left px-2 py-1 text-[10px]">Pattern</th>
                <th className="text-left px-2 py-1 text-[10px]">Location</th>
                <th className="text-left px-2 py-1 text-[10px]">Group</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {grepExtract.map((rule, i) => (
                <tr key={i} className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <td className={`px-2 py-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{rule.name || '-'}</td>
                  <td className={`px-2 py-1 font-mono ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{rule.pattern}</td>
                  <td className={`px-2 py-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{rule.location}</td>
                  <td className={`px-2 py-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{rule.group}</td>
                  <td>
                    <button onClick={() => removeExtractRule(i)} className="text-red-400 hover:text-red-300 p-1">
                      <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Add new extract rule */}
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className={labelClass}>Name</label>
            <input
              value={newExtractRule.name}
              onChange={e => setNewExtractRule(p => ({ ...p, name: e.target.value }))}
              className={`w-full ${inputClass}`}
              placeholder="Column name"
            />
          </div>
          <div className="flex-1">
            <label className={labelClass}>Pattern (regex with groups)</label>
            <input
              value={newExtractRule.pattern}
              onChange={e => setNewExtractRule(p => ({ ...p, pattern: e.target.value }))}
              className={`w-full ${inputClass} font-mono`}
              placeholder="token[=:](.+?)[\s&quot;]"
            />
          </div>
          <div>
            <label className={labelClass}>Location</label>
            <select
              value={newExtractRule.location}
              onChange={e => setNewExtractRule(p => ({ ...p, location: e.target.value }))}
              className={inputClass}
            >
              <option value="response_body">Response body</option>
              <option value="response_headers">Response headers</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Group #</label>
            <input
              type="number"
              value={newExtractRule.group}
              onChange={e => setNewExtractRule(p => ({ ...p, group: Number(e.target.value) }))}
              min={0}
              max={10}
              className={`w-16 ${inputClass}`}
            />
          </div>
          <button
            onClick={addExtractRule}
            disabled={!newExtractRule.pattern}
            className="px-3 py-1.5 rounded text-xs bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default IntruderOptions;
